/*
 * AI-5. The learner's record, as the Goals Report's "Insights from Via" reads
 * it.
 *
 * SHARED ON PURPOSE, like credential-ai-rank.ts. Two places build the report
 * payload: the browser mapper (every report a learner asks for) and the
 * subgraph's AIService.getSurveyData (the regeneration after a survey is
 * retaken). They must describe the same learner the same way, or a report
 * reads differently depending on which path happened to write it.
 *
 * Pure. No Angular, no Nest, no database. Plain structural inputs, because the
 * browser's shapes and the subgraph's shapes are the same GraphQL types seen
 * from two sides.
 *
 * WHY THIS EXISTS. From 10 Sep until AI-5 the report model was sent an empty
 * skills list (the mapper read `occupation.skills`, which the query had
 * stopped selecting), education as a bare level, and the civilian sentinel MOC
 * as a job called "Learner". With nothing specific to work from it wrote
 * sentences like "The analysis does not identify which skills account for it."
 */

/** One KSA row as `CPDOccupation.ksas` returns it, with the OCC.3 match fields. */
export interface ReportKsaRow {
  id?: string | null;
  name?: string | null;
  type?: string | null;
  importance?: number | null;
  matchStatus?: string | null;
  matchSources?: { channel?: string | null; name?: string | null }[] | null;
  displayTier?: string | null;
}

/** `CareerGoalSkillCompatibilityInput`. */
export interface ReportSkill {
  name: string;
  isUserMatch: boolean;
  type: string;
  importance?: number;
  /** MET | NAMED | GAP, or null when the record was not compared. */
  matchStatus: 'MET' | 'NAMED' | 'GAP' | null;
  matchedBy?: string[];
}

export interface ReportReadinessArea {
  area: string;
  isMatched?: boolean;
  required: string[];
  learnerHas: string[];
}

export interface GoalsReportRecordSource {
  militaryBranch?: { branchCode?: string | null; branchTitle?: string | null } | null;
  userRanks?: { payGrade?: string | null; title?: string | null }[] | null;
  userMocs?: { mocCode?: string | null; mocTitle?: string | null; numberOfMonthsOfWorkExperience?: number | null }[] | null;
  userWorkExperiences?: { occupationTitle?: string | null; numberOfMonthsOfWorkExperience?: number | null }[] | null;
  userEducations?: {
    educationLevelShortDescr?: string | null;
    degreeInfo?: { title?: string | null } | null;
    completed?: boolean | null;
  }[] | null;
  ksas?: ReportKsaRow[] | null;
  /** The occupation's detailed work activities, matched to the learner. */
  skillsCompatibility?: { text?: string | null; matchesUser?: boolean | null }[] | null;
  readinessEvaluation?: {
    itemType?: number | null;
    isPositiveOutcome?: boolean | null;
    occupationRequiresObject?: {
      title?: string | null;
      details?: string | null;
      detailsArray?: string[] | null;
      isMatched?: boolean | null;
    }[] | null;
    userHasObject?: { title?: string | null; details?: string | null }[] | null;
  }[] | null;
}

/** The record fields of `SkillsCompatibilityAndGoalSelectionInput`. */
export interface GoalsReportRecord {
  userWorkExperiences: string[];
  userEducationExperiences: string[];
  userCivilianWorkExperiences: string[];
  careerGoalSkillCompatibility: ReportSkill[];
  militaryBackground?: { branch?: string; rank?: string; payGrade?: string };
  workActivitiesMatch?: { text: string; matchesUser: boolean }[];
  readiness?: ReportReadinessArea[];
}

/**
 * How much of each list goes to the model.
 *
 * Bounded so a 114-row KSA profile does not become the whole prompt. The
 * server already orders held rows by importance and gap rows biggest-gap first
 * (OCC.5), so the first N are the ones worth talking about.
 */
export const REPORT_SKILL_LIMITS = {
  held: 12,
  gap: 12,
  unassessed: 10,
  workActivities: 12,
  programsPerRequirement: 4,
};

/**
 * The four families, as an allowlist. `software` is a real element_type and
 * it is exactly the banned content (software product names), so anything not
 * listed is dropped rather than passed through. Same rule as familyLabelFor in
 * the Skills tab.
 */
const FAMILIES: Record<string, string> = {
  knowledge: 'knowledge',
  skill: 'skill',
  skills: 'skill',
  ability: 'ability',
  abilities: 'ability',
  work_activity: 'work_activity',
  work_activities: 'work_activity',
};

const CHANNEL_WORDS: Record<string, string> = {
  MILITARY: 'military experience',
  WORK: 'work experience',
  EDUCATION: 'education',
  CREDENTIAL: 'credential',
  TRAINING: 'military training',
};

function clean(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function matchedBy(sources: ReportKsaRow['matchSources']): string[] | undefined {
  const described = (sources ?? [])
    .map((source) => {
      const words = CHANNEL_WORDS[clean(source?.channel).toUpperCase()] ?? 'your record';
      const name = clean(source?.name);
      return name ? `${words}: ${name}` : words;
    })
    .filter((text, index, all) => all.indexOf(text) === index)
    .slice(0, 2);
  return described.length ? described : undefined;
}

function toSkill(row: ReportKsaRow, type: string, matchStatus: ReportSkill['matchStatus']): ReportSkill {
  const skill: ReportSkill = {
    name: clean(row.name),
    isUserMatch: matchStatus === 'MET' || matchStatus === 'NAMED',
    type,
    ...(typeof row.importance === 'number' ? { importance: row.importance } : {}),
    matchStatus,
  };
  const by = skill.isUserMatch ? matchedBy(row.matchSources) : undefined;
  return by ? { ...skill, matchedBy: by } : skill;
}

/**
 * The occupation's KSA rows as the report should talk about them, using the
 * Skills tab's own rules (readiness-ksa-match.ts) so the text and the page
 * never disagree:
 *
 * - MET and NAMED are things the learner has.
 * - GAP is a gap, and so is UNMEASURED for a learner whose record rated
 *   anything (MET or GAP present).
 * - For a learner whose record rated nothing, no row is a gap. The top
 *   requirements go with no status, so the report can say what the
 *   occupation asks for without inventing a shortfall.
 * - BASELINE is hidden on the page and dropped here.
 */
export function selectReportSkills(rows: ReportKsaRow[] | null | undefined): ReportSkill[] {
  const typed = (rows ?? [])
    .map((row) => ({ row, type: FAMILIES[clean(row?.type).toLowerCase()] }))
    .filter(({ row, type }) => !!type && !!clean(row?.name));
  const status = (row: ReportKsaRow) => clean(row.matchStatus).toUpperCase();
  const readable = typed.some(({ row }) => status(row) === 'MET' || status(row) === 'GAP');

  const held = typed
    .filter(({ row }) => status(row) === 'MET' || status(row) === 'NAMED')
    .slice(0, REPORT_SKILL_LIMITS.held)
    .map(({ row, type }) => toSkill(row, type, status(row) as 'MET' | 'NAMED'));

  if (!readable) {
    const unassessed = typed
      .filter(({ row }) => !status(row) || status(row) === 'UNMEASURED')
      .slice(0, REPORT_SKILL_LIMITS.unassessed)
      .map(({ row, type }) => toSkill(row, type, null));
    return [...held, ...unassessed];
  }

  const gaps = typed
    .filter(({ row }) => status(row) === 'GAP' || status(row) === 'UNMEASURED')
    .slice(0, REPORT_SKILL_LIMITS.gap)
    .map(({ row, type }) => toSkill(row, type, 'GAP'));
  return [...held, ...gaps];
}

/** "Bachelor's Degree in Computer Science (completed)". */
export function describeEducation(education: NonNullable<GoalsReportRecordSource['userEducations']>[number]): string {
  const level = clean(education?.educationLevelShortDescr);
  if (!level) {
    return '';
  }
  const field = clean(education?.degreeInfo?.title);
  // GR-1: rows written before the survey asked are attainment, so only an
  // explicit false reads as unfinished.
  const state = education?.completed === false ? 'in progress' : 'completed';
  return `${field ? `${level} in ${field}` : level} (${state})`;
}

/**
 * CPD-662's sentinel: the survey writes MOC "X" titled "Learner" for somebody
 * who has not served. Matched on both fields, as isCivilianMocMarker in
 * member-identity.const.ts does, so a real unrecognised code still goes.
 */
function isCivilianMocMarker(code: string, title: string): boolean {
  return code.toUpperCase() === 'X' && title === 'Learner';
}

/** The branch row civilians get: code "1", titled "Military Spouse". */
const CIVILIAN_BRANCH_CODE = '1';

/** The rank the skills survey writes when none is given (CPD-662). */
const RANK_NOT_GIVEN_TITLE = 'Not given';

const PAY_GRADE = /^[EOW]-?\d{1,2}$/i;

function withMonths(title: string, months: number | null | undefined): string {
  return typeof months === 'number' && months > 0 ? `${title}, ${months} months` : title;
}

function militaryBackground(source: GoalsReportRecordSource): GoalsReportRecord['militaryBackground'] {
  const branchCode = clean(source.militaryBranch?.branchCode);
  const branch = branchCode && branchCode !== CIVILIAN_BRANCH_CODE ? clean(source.militaryBranch?.branchTitle) : '';
  if (!branch) {
    return undefined;
  }
  const rank = (source.userRanks ?? []).find((r) => clean(r?.title) && clean(r?.title) !== RANK_NOT_GIVEN_TITLE);
  const title = clean(rank?.title);
  const payGrade = clean(rank?.payGrade);
  return {
    branch,
    ...(title ? { rank: title } : {}),
    ...(PAY_GRADE.test(payGrade) ? { payGrade: payGrade.toUpperCase() } : {}),
  };
}

const AREA_NAMES: Record<number, string> = {
  0: 'Education',
  1: 'Work experience',
  2: 'Training',
  3: 'Certification',
  4: 'Licensing',
};

/** A holding that is the server's "nothing on record" sentence, not a record. */
const NO_INFORMATION = /^we (?:currently )?have no information/i;

function describeRequirement(req: {
  title?: string | null;
  details?: string | null;
  detailsArray?: string[] | null;
}): string {
  const title = clean(req.title);
  const programs = (req.detailsArray ?? []).map(clean).filter(Boolean).slice(0, REPORT_SKILL_LIMITS.programsPerRequirement);
  if (programs.length) {
    return `${title} in ${programs.join(' or ')}`;
  }
  const details = clean(req.details);
  return details ? `${title} (${details})` : title;
}

function readiness(source: GoalsReportRecordSource): ReportReadinessArea[] | undefined {
  const areas = (source.readinessEvaluation ?? [])
    .map((evaluation): ReportReadinessArea | null => {
      const area = AREA_NAMES[evaluation?.itemType ?? -1];
      const requires = evaluation?.occupationRequiresObject ?? [];
      const required = requires.filter((r) => clean(r?.title)).map(describeRequirement);
      if (!area || !required.length) {
        return null;
      }
      const learnerHas = (evaluation?.userHasObject ?? [])
        .filter((h) => clean(h?.title) && !NO_INFORMATION.test(clean(h?.title)))
        .map((h) => (clean(h.details) ? `${clean(h.title)} (${clean(h.details)})` : clean(h.title)));
      const isMatched =
        typeof evaluation?.isPositiveOutcome === 'boolean'
          ? evaluation.isPositiveOutcome
          : requires.every((r) => !!r?.isMatched);
      return { area, isMatched, required, learnerHas };
    })
    .filter((a): a is ReportReadinessArea => !!a);
  return areas.length ? areas : undefined;
}

export function buildGoalsReportRecord(source: GoalsReportRecordSource): GoalsReportRecord {
  const record: GoalsReportRecord = {
    userWorkExperiences: (source.userMocs ?? [])
      .filter((m) => clean(m?.mocTitle) && !isCivilianMocMarker(clean(m?.mocCode), clean(m?.mocTitle)))
      .map((m) => withMonths([clean(m.mocCode), clean(m.mocTitle)].filter(Boolean).join(' '), m.numberOfMonthsOfWorkExperience)),
    userEducationExperiences: (source.userEducations ?? []).map(describeEducation).filter(Boolean),
    userCivilianWorkExperiences: (source.userWorkExperiences ?? [])
      .filter((w) => clean(w?.occupationTitle))
      .map((w) => withMonths(clean(w.occupationTitle), w.numberOfMonthsOfWorkExperience)),
    careerGoalSkillCompatibility: selectReportSkills(source.ksas),
  };

  const background = militaryBackground(source);
  if (background) {
    record.militaryBackground = background;
  }

  // OCC.4, 28 Sep. `matchesUser: null` is a row nothing on the record could
  // be checked against. It is neither covered nor uncovered, and the input
  // type is Boolean!, so it is left out rather than sent to Via as a gap.
  const activities = (source.skillsCompatibility ?? [])
    .filter((a) => clean(a?.text) && a?.matchesUser !== null)
    .slice(0, REPORT_SKILL_LIMITS.workActivities)
    .map((a) => ({ text: clean(a.text), matchesUser: !!a.matchesUser }));
  if (activities.length) {
    record.workActivitiesMatch = activities;
  }

  const areas = readiness(source);
  if (areas) {
    record.readiness = areas;
  }

  return record;
}
